import { useState, Suspense } from "react";
import {
  BoxHeader,
  BoxNavLeft,
  BoxContent,
  BoxTitle,
  BoxNavLink,
  BoxNavRight,
  BoxNavButton,
  LoadingBoxContent,
  Box,
} from "./Box";
import { Switch, Route, Redirect } from "react-router";
import SidePanel from "./SidePanel";
import { Close, ExclamationPointCircle } from "./Icons";
import ContactEditor from "./ContactEditor";
import ContactNotes from "./ContactNotes";
import { ContactScheduledMessages } from "./ScheduledMessages";
import MilestoneAlert from "./milestones/MilestoneAlert";
import Title from "./Title";
import ContactMessages from "./ContactMessages";
import useWindowSize from "../hooks/use-screen-size";
import { ERROR_COLOR, WARNING_COLOR } from "../vars";
import { BackButton } from "./Content";
import { useSkipioQuery } from "../hooks/use-query";
import { useQuery } from "@tanstack/react-query";
import { fetchUser } from "../api/users";
import { fetchContactScheduledMessages } from "../api/messages";

function ContactViewer({
  contactId,
  scope = "inbox",
  hide,
  tabs = true,
  showInfoButton = false,
}) {
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data;
  const contact = useSkipioQuery({ queryKey: ["contacts", contactId] }).data;

  const [showMilestone] = useState(false);
  const [lastMilestone] = useState(null);

  const countScheduledMessages =
    useQuery({
      queryKey: ["messages", "scheduled", { contactId }],
      queryFn: () => fetchContactScheduledMessages(contactId),
      select: (queryData) => queryData.meta,
      suspense: false,
      useErrorBoundary: true,
    }).data?.totalCount || 0;

  const countContactNotes =
    useSkipioQuery({
      queryKey: ["contacts", contactId, "contactNotes", { page: 1 }],
      suspense: false,
      useErrorBoundary: true,
    }).meta?.totalCount || 0;

  return (
    <>
      <MilestoneAlert active={showMilestone} fill>
        {Boolean(lastMilestone) && <Title>{lastMilestone.name}</Title>}
      </MilestoneAlert>
      <BoxHeader>
        <BoxNavLeft>
          <BackButton />
          <BoxTitle data-ignore="true">{contact.fullName}</BoxTitle>
        </BoxNavLeft>
        <BoxNavRight>
          {tabs ? (
            <>
              <BoxNavLink to={`/app/messages/${scope}/${contactId}/messages`}>
                Messages
              </BoxNavLink>
              <BoxNavLink to={`/app/messages/${scope}/${contactId}/info`}>
                Info
              </BoxNavLink>
              <BoxNavLink
                data-badge={countContactNotes || undefined}
                badgePriority="low"
                to={`/app/messages/${scope}/${contactId}/notes`}
              >
                Notes
              </BoxNavLink>
              <BoxNavLink
                data-badge={countScheduledMessages || undefined}
                badgePriority="high"
                to={`/app/messages/${scope}/${contactId}/scheduled`}
              >
                Scheduled
              </BoxNavLink>
            </>
          ) : (
            <>
              {showInfoButton && (
                <SidePanel
                  trigger={({ ref, show }) => (
                    <BoxNavButton ref={ref} onClick={show}>
                      Info
                    </BoxNavButton>
                  )}
                  content={({ hide }) => (
                    <>
                      <BoxHeader stacked>
                        <BoxNavRight>
                          <BoxNavButton onClick={hide}>
                            <Close />
                          </BoxNavButton>
                        </BoxNavRight>
                      </BoxHeader>
                      <Suspense fallback={<LoadingBoxContent />}>
                        <ContactEditor contactId={contactId} />
                      </Suspense>
                    </>
                  )}
                />
              )}
              <SidePanel
                trigger={({ ref, show }) => (
                  <BoxNavButton
                    ref={ref}
                    onClick={show}
                    data-badge={countContactNotes || undefined}
                    badgePriority="low"
                  >
                    Notes
                  </BoxNavButton>
                )}
                content={({ hide }) => (
                  <>
                    <BoxHeader>
                      <BoxNavLeft>
                        <BoxTitle>Notes</BoxTitle>
                      </BoxNavLeft>
                      <BoxNavRight>
                        <BoxNavButton onClick={hide}>
                          <Close />
                        </BoxNavButton>
                      </BoxNavRight>
                    </BoxHeader>
                    <Suspense fallback={null}>
                      <BoxContent>
                        <ContactNotes contactId={contactId} />
                      </BoxContent>
                    </Suspense>
                  </>
                )}
              />
              <SidePanel
                trigger={({ ref, show }) => (
                  <BoxNavButton
                    ref={ref}
                    onClick={show}
                    data-badge={countScheduledMessages || undefined}
                    badgePriority="high"
                  >
                    Scheduled
                  </BoxNavButton>
                )}
                content={({ hide }) => (
                  <>
                    <BoxHeader>
                      <BoxNavLeft>
                        <BoxTitle>Scheduled Messages</BoxTitle>
                      </BoxNavLeft>
                      <BoxNavRight>
                        <BoxNavButton onClick={hide}>
                          <Close />
                        </BoxNavButton>
                      </BoxNavRight>
                    </BoxHeader>
                    <Suspense fallback={null}>
                      <BoxContent>
                        <ContactScheduledMessages contactId={contactId} />
                      </BoxContent>
                    </Suspense>
                  </>
                )}
              />
            </>
          )}
          {Boolean(hide) && (
            <BoxNavButton onClick={hide}>
              <Close />
            </BoxNavButton>
          )}
        </BoxNavRight>
      </BoxHeader>
      {user.id !== contact.assignedToUserId && (
        <BoxHeader white>
          <BoxTitle>
            <ExclamationPointCircle
              style={{ color: WARNING_COLOR, marginRight: "10px" }}
            />{" "}
            {contact.fullName} is assigned to {contact.assignedTo}
          </BoxTitle>
        </BoxHeader>
      )}
      {contact.isSmsBlacklisted && (
        <BoxHeader white>
          <BoxTitle>
            <ExclamationPointCircle
              style={{ color: WARNING_COLOR, marginRight: "10px" }}
            />{" "}
            {contact.fullName} has opted out of messages
          </BoxTitle>
        </BoxHeader>
      )}
      {contact.isBlocked && (
        <BoxHeader white>
          <BoxTitle>
            <ExclamationPointCircle
              style={{ color: ERROR_COLOR, marginRight: "10px" }}
            />{" "}
            {contact.fullName} is blocked
          </BoxTitle>
        </BoxHeader>
      )}
      {!contact.isSmsEnabled && (
        <BoxHeader white>
          <BoxTitle>
            <ExclamationPointCircle
              style={{ color: WARNING_COLOR, marginRight: "10px" }}
            />{" "}
            {contact.fullName} will not be sent messages
          </BoxTitle>
        </BoxHeader>
      )}
      {contact.isInvalidPhoneNumber && (
        <BoxHeader white>
          <BoxTitle>
            <ExclamationPointCircle
              style={{ color: WARNING_COLOR, marginRight: "10px" }}
            />{" "}
            {contact.fullName} does not have a valid phone number
          </BoxTitle>
        </BoxHeader>
      )}
      <Switch>
        <Route path={`/app/messages/${scope}/${contactId}/messages`}>
          <ContactMessages contactId={contactId} />
        </Route>
        <Route path={`/app/messages/${scope}/${contactId}/info`}>
          <Suspense fallback={<LoadingBoxContent />}>
            <ContactEditor contactId={contactId} />
          </Suspense>
        </Route>
        <Route path={`/app/messages/${scope}/${contactId}/notes`}>
          <Suspense fallback={<LoadingBoxContent />}>
            <BoxContent>
              <ContactNotes contactId={contactId} />
            </BoxContent>
          </Suspense>
        </Route>
        <Route path={`/app/messages/${scope}/${contactId}/scheduled`}>
          <Suspense fallback={null}>
            <BoxContent>
              <ContactScheduledMessages contactId={contactId} />
            </BoxContent>
          </Suspense>
        </Route>
        <Redirect to={`/app/messages/${scope}/${contactId}/info`} />
      </Switch>
    </>
  );
}

export function ContactViewerDouble({ contactId, scope }) {
  const { width } = useWindowSize();
  const showSide = width > 1600;
  const sideWidth = Math.min(Math.max(width * 0.25, 400), 700);

  return (
    <>
      <Box>
        <ContactViewer
          contactId={contactId}
          scope={scope}
          key={contactId}
          tabs={false}
          showInfoButton={!showSide}
        />
      </Box>
      {showSide && (
        <Box style={{ flexGrow: 0, flexShrink: 0, width: `${sideWidth}px` }}>
          <Suspense fallback={<LoadingBoxContent />}>
            <ContactEditor contactId={contactId} />
          </Suspense>
        </Box>
      )}
    </>
  );
}

export function ContactViewerSidePanel({
  trigger,
  contactId,
  isShowing = false,
  onHide = () => {},
}) {
  return (
    <SidePanel
      trigger={trigger}
      isShowing={isShowing}
      onHide={onHide}
      content={({ hide }) => {
        return contactId ? (
          <ContactViewer hide={hide} contactId={contactId} />
        ) : (
          <LoadingBoxContent />
        );
      }}
    />
  );
}

export default ContactViewer;
